/** PM Typing Sender — sends a typing frame over the websocket while the user types. */

import { createTimeoutSignal } from '../../modules/timeout-signal.js';
import { decodeJwtPayload } from '../../modules/jwt-payload.js';
import { monotonicNow } from '../../modules/monotonic-now.js';

const THROTTLE_MS = 3000;

// The frame carries this outside the token, so the only thing that bounds it is
// the range websocketerd enforces in internal/rpc_thread_typing.go, which the
// REST typingAction mirrors.
const TYPING_DELAY = 3000;

// Refresh this far ahead of the expiry so a frame never carries a token
// websocketerd has already stopped accepting. The held token keeps being sent
// while the refresh runs, so a failed refresh costs no typing.
const REFRESH_MARGIN_MS = 60000;

// One refresh per thread per minute, whatever the freshness math says. A client
// clock that runs fast reads every held token as stale.
const REFRESH_MIN_INTERVAL_MS = 60000;

// A request that never answers must not hold the thread's one refresh slot.
const REFRESH_TIMEOUT_MS = 5000;

// threadId -> { token, expiresAt } in epoch milliseconds.
const heldTokens = new Map();
// threadId -> the earliest moment the next refresh may start, monotonic ms.
const refreshAllowedAt = new Map();
// The thread keys with a send already waiting on a refresh. Per thread, so a
// stalled request on one conversation never holds up another that has its
// token, and a burst of keystrokes makes one request and one frame.
const sendsInFlight = new Set();

// -Infinity, not 0: monotonicNow() can start near zero, so a zero would read
// as a frame sent at page load and swallow the first typing window.
let lastSentAt = -Infinity;
let currentThreadId = null;

/**
 * The moment a token stops verifying, on this browser's own clock.
 *
 * The lifetime is `exp - iat`, two server moments, added to the moment the
 * token arrived. A wrong browser clock does not move that deadline.
 *
 * @return {number|null} Epoch milliseconds, or null when the claims are unreadable
 */
function typingTokenDeadline(token, receivedAtMs) {
  let claims;
  try {
    claims = decodeJwtPayload(String(token));
  } catch {
    return null;
  }

  if (!claims || typeof claims.iat !== 'number' || typeof claims.exp !== 'number') return null;

  return receivedAtMs + (claims.exp - claims.iat) * 1000;
}

function setThreadTypingToken(threadId, token, expires) {
  if (!threadId || !token) return;

  let expiresAt = typingTokenDeadline(token, Date.now());

  if (expiresAt === null) {
    expiresAt = Date.parse(expires);
    // Without a readable expiry there is no moment to stop sending the token
    // on, so the thread keeps whatever it holds and refreshes next window.
    if (Number.isNaN(expiresAt)) return;
  }

  heldTokens.set(String(threadId), { token, expiresAt });
}

/**
 * Forget one thread's token, so its next typing window fetches a fresh one.
 *
 * websocketerd calls this thread's token invalid. The minimum refresh interval
 * still applies, so a server that refuses every frame costs one request a
 * minute.
 */
function clearThreadTypingToken(threadId) {
  heldTokens.delete(String(threadId));
}

window.clearThreadTypingToken = clearThreadTypingToken;

/** Store the token off any thread-open response, keyed by thread id. */
function storeThreadTypingToken(threadId, data) {
  if (!threadId || !data || !data.typing_token) return;

  setThreadTypingToken(String(threadId), data.typing_token, data.typing_token_expires);
}

window.storeThreadTypingToken = storeThreadTypingToken;

async function fetchThreadTypingToken(threadId) {
  if (!window.ApiClient) return;

  // ApiClient reads the stored JWT and never mints one, so a page whose token
  // bootstrap has not landed would send this unauthenticated and take a 401.
  // window.getApiToken mints, the way web-push.js reaches the same endpoint.
  if (!window.ApiClient.getApiToken?.() && !(await window.getApiToken?.())) return;

  const timeout = createTimeoutSignal(REFRESH_TIMEOUT_MS);

  try {
    // ApiClient spreads its options into fetch, so the signal reaches the request.
    const response = await window.ApiClient.request('/thread/' + encodeURIComponent(threadId), { signal: timeout.signal });
    if (!response.ok) return;

    storeThreadTypingToken(threadId, await response.json());
  } finally {
    timeout.clear();
  }
}

/**
 * Start a refresh for one thread, or return null when the minimum interval has
 * not elapsed.
 *
 * The window is claimed before the request goes out, so a refresh that throws,
 * aborts, or answers without a token backs off the same way a successful one
 * does. One send per thread runs at a time, so there is never a second refresh
 * to join.
 */
function startThreadTypingRefresh(threadId) {
  if ((refreshAllowedAt.get(threadId) || 0) > monotonicNow()) return null;
  refreshAllowedAt.set(threadId, monotonicNow() + REFRESH_MIN_INTERVAL_MS);

  // Typing is best-effort, so a rejected refresh settles as a no-op and the
  // caller falls back to whatever the thread already holds.
  return fetchThreadTypingToken(threadId).catch(() => {});
}

/** The token to send on, or null. Expiry is a wall-clock comparison. */
async function getThreadTypingToken(threadId) {
  const held = heldTokens.get(threadId);

  if (held && held.expiresAt - Date.now() > REFRESH_MARGIN_MS) {
    return held;
  }

  const refresh = startThreadTypingRefresh(threadId);

  // A token inside the margin still verifies. Send on it now and let the
  // refresh land in the background for the windows after this one.
  if (held && held.expiresAt > Date.now()) {
    return held;
  }

  if (!refresh) return null;
  await refresh;

  // A failed refresh leaves the expired entry in place. websocketerd rejects it.
  const refreshed = heldTokens.get(threadId);

  return refreshed && refreshed.expiresAt > Date.now() ? refreshed : null;
}

async function sendTypingIndicator(threadId) {
  const key = String(threadId);
  if (sendsInFlight.has(key)) return;
  if (monotonicNow() - lastSentAt < THROTTLE_MS) return;

  sendsInFlight.add(key);
  try {
    const held = await getThreadTypingToken(key);
    if (!held || !window.sendWebSocketRpc) return;

    // The user can switch conversations while a refresh is in flight. This
    // token names the thread they left, and the frame would report typing in it.
    const openThreadId = window.get_active_thread_id ? window.get_active_thread_id() : null;
    if (!openThreadId || String(openThreadId) !== key) return;

    const sent = window.sendWebSocketRpc({
      // The reply carries this id back, which is how a refusal names its thread.
      id: 'typing:' + key + ':' + Date.now(),
      method: 'thread-typing',
      parameters: [held.token, TYPING_DELAY]
    });

    // The window is spent on a frame the socket took, never on a lookup.
    if (sent === true) {
      lastSentAt = monotonicNow();
    }
  } finally {
    sendsInFlight.delete(key);
  }
}

$(document).on('input', '#message', function () {
  const threadId = window.get_active_thread_id ? window.get_active_thread_id() : null;
  if (!threadId) return;

  // Reset throttle when switching threads
  if (String(threadId) !== currentThreadId) {
    currentThreadId = String(threadId);
    lastSentAt = -Infinity;
  }

  sendTypingIndicator(threadId);
});
